import { useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { useCallback, useState } from "react";
import { hasDcrConfiguration } from "../../../shared/connectors/dcr";
import type { ConnectorDefinition } from "../../../shared/connectors/types";
import { sharedMutators } from "../../../shared/mutators";
import { useFormatRelativeTime } from "../../hooks/useFormatRelativeTime";
import { oauthSetupError } from "../../lib/connectorStatus";
import { toSentence } from "../../lib/toSentence";
import { Box } from "../Box";
import { Button } from "../Button";
import { ConfirmModal } from "../ConfirmModal";
import { Icon } from "../Icon";
import { Input } from "../Input";
import { IntegrationButton } from "../IntegrationButton";
import { RefreshSpin } from "../RefreshSpin";
import { Text } from "../Text";
import { Tooltip } from "../Tooltip";
import {
  Actions,
  AuthorizedButton,
  AuthorizedInfo,
  AuthorizedLabel,
  CheckIcon,
  IconButton,
} from "./authorizedChip";
import type { App } from "./Form";
import type { ConnectorAuthorize } from "./useConnectorAuthorize";

const Credentials = styled("div")`
  display: flex;
  flex-direction: column;
  margin-bottom: 12px;
`;

const CancelRow = styled("div")`
  display: flex;
  justify-content: center;
  margin-top: 8px;
`;

type Props = {
  app: App;
  definition: ConnectorDefinition;
  disabled?: boolean;
  auth: ConnectorAuthorize;
};

export function OAuthFieldItem({ app, definition, disabled, auth }: Props) {
  const z = useZero();
  const [confirmResetOpen, setConfirmResetOpen] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [reauthenticating, setReauthenticating] = useState(false);

  const resetConnection = useCallback(() => {
    if (!app.oauthConnectionId) return;
    setConfirmResetOpen(true);
  }, [app.oauthConnectionId]);

  const oauthStatus = app.oauthConnection?.status;
  const oauthAuthorized =
    oauthStatus && ["connected", "obtained_code"].includes(oauthStatus);
  const oauthError = app.oauthConnection?.lastError;
  const authorizedAt = app.oauthConnection?.updatedAt;
  const formatRelativeTime = useFormatRelativeTime();
  if (app.authorizationMode === "visitor") {
    return definition.oauth?.clientCredentialsFrom === "dcr" ? (
      <OAuthClientSetup
        app={app}
        definition={definition}
        disabled={disabled}
        remaining={auth.remaining}
      />
    ) : null;
  }
  const grantType = definition.oauth?.grantType(app.configuration);
  const passwordGrant = grantType === "password";
  const actionText =
    grantType === "client_credentials" || passwordGrant
      ? `Connect with ${definition.name}`
      : `Authorize with ${definition.name}`;

  const clearCredentials = () => {
    setUsername("");
    setPassword("");
    setReauthenticating(false);
  };

  const submit = async () => {
    const result = await auth.authorize(
      passwordGrant ? { username, password } : undefined
    );
    if (result === "success") clearCredentials();
  };

  if (oauthAuthorized && !reauthenticating) {
    return (
      <div>
        <AuthorizedButton>
          <AuthorizedLabel>
            <img src={definition.logo} alt={definition.name} />
            <Text variant="text-regular-md">
              Authorized with {definition.name}
            </Text>
          </AuthorizedLabel>
          <Actions>
            <Tooltip content="Replace authorization">
              <IconButton
                type="button"
                onClick={() =>
                  passwordGrant ? setReauthenticating(true) : submit()
                }
                disabled={disabled || auth.isPending}
              >
                <RefreshSpin spinning={auth.isPending}>
                  <Icon name="refresh-cw" size={16} />
                </RefreshSpin>
              </IconButton>
            </Tooltip>
            <Tooltip content="Remove authorization">
              <IconButton
                type="button"
                onClick={() => resetConnection()}
                disabled={disabled}
              >
                <Icon name="circle-minus" size={16} />
              </IconButton>
            </Tooltip>
          </Actions>
        </AuthorizedButton>

        {authorizedAt && (
          <AuthorizedInfo>
            <CheckIcon />
            <Text variant="text-small" color="var(--text-secondary)">
              Authorized {formatRelativeTime(authorizedAt)}
            </Text>
          </AuthorizedInfo>
        )}
        <ConfirmModal
          open={confirmResetOpen}
          onClose={() => setConfirmResetOpen(false)}
          onConfirm={() => {
            if (app.oauthConnectionId) {
              z.mutate(
                sharedMutators.resetOAuthConnection({
                  oauthConnectionId: app.oauthConnectionId,
                })
              );
            }
            setConfirmResetOpen(false);
          }}
          title="Disconnect connector"
          description={`Disconnects this connector immediately. You’ll need to authorize it again to keep using it.`}
          confirmLabel="Disconnect"
          destructive
        />
      </div>
    );
  }

  return (
    <div>
      {passwordGrant && (
        <Credentials>
          <Box horizontal={8} vertical={10}>
            <Text variant="text-small-md" color="var(--text-primary)">
              Username
            </Text>
          </Box>
          <Input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="service-account"
            style={{ width: "100%" }}
            disabled={disabled}
          />
          <Box horizontal={8} vertical={10}>
            <Text variant="text-small-md" color="var(--text-primary)">
              Password
            </Text>
          </Box>
          <Input
            secret
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={{ width: "100%" }}
            disabled={disabled}
          />
        </Credentials>
      )}
      <IntegrationButton
        type="button"
        onClick={submit}
        disabled={
          disabled ||
          auth.remaining.length > 0 ||
          (passwordGrant && (!username || !password))
        }
        isLoading={auth.isPending}
        logo={definition.logo}
        text={actionText}
      />
      {reauthenticating && (
        <CancelRow>
          <Button
            variant="subtle"
            type="button"
            onClick={clearCredentials}
            disabled={disabled}
          >
            Cancel
          </Button>
        </CancelRow>
      )}
      {auth.remaining.length > 0 && (
        <AuthorizedInfo>
          <Icon name="info" size={12} color="var(--text-secondary)" />
          <Text variant="text-small" color="var(--text-secondary)">
            Fill out {toSentence(auth.remaining.map((t) => t.field))} first
          </Text>
        </AuthorizedInfo>
      )}
      {(auth.authError || (oauthStatus === "error" && oauthError)) && (
        <AuthorizedInfo>
          <Icon name="info" size={12} />
          <Text variant="text-small" color="var(--text-error)">
            {auth.authError ?? oauthError}
          </Text>
        </AuthorizedInfo>
      )}
    </div>
  );
}

function OAuthClientSetup({
  app,
  definition,
  disabled,
  remaining,
}: Omit<Props, "auth"> & { remaining: ConnectorAuthorize["remaining"] }) {
  const z = useZero();
  const [requestedFor, setRequestedFor] = useState<string | null>(null);
  const setupInputs = JSON.stringify([app.configuration, app.oauthClientId]);
  const error = oauthSetupError(app);

  if (
    hasDcrConfiguration(definition.oauth!, app.configuration, app.oauthClientId)
  ) {
    return (
      <AuthorizedInfo>
        <CheckIcon />
        <Text variant="text-small" color="var(--text-secondary)">
          OAuth client registered. Each person connects their own account.
        </Text>
      </AuthorizedInfo>
    );
  }

  return (
    <div>
      <IntegrationButton
        type="button"
        onClick={() => {
          setRequestedFor(setupInputs);
          z.mutate(
            sharedMutators.prepareConnectorOAuthClient({
              connectedAppId: app.id,
            })
          );
        }}
        disabled={disabled || remaining.length > 0}
        isLoading={requestedFor === setupInputs && !error}
        logo={definition.logo}
        text="Discover OAuth server"
      />
      {remaining.length > 0 ? (
        <AuthorizedInfo>
          <Icon name="info" size={12} color="var(--text-secondary)" />
          <Text variant="text-small" color="var(--text-secondary)">
            Fill out {toSentence(remaining.map((t) => t.field))} first
          </Text>
        </AuthorizedInfo>
      ) : error ? (
        <AuthorizedInfo>
          <Icon name="info" size={12} />
          <Text variant="text-small" color="var(--text-error)">
            {error}
          </Text>
        </AuthorizedInfo>
      ) : null}
    </div>
  );
}
